/**
 * The memory Timeline (D340, docs/plans/2026-10-memory-globe.md): every item by its last change, on one calendar axis.
 * No DOM.
 *
 * Ported from RUBRIC Agentic OS v2 (Jay E, RoboNuggets, MIT; see THIRD-PARTY-NOTICES.md), brain/timeline-layout.js
 * `build`: one shared axis for every lane (a date has exactly one x in every lane), equal-width days in month blocks
 * (the anchor's month and the two before it), an "Older" block for everything before and for what has no date, crowded
 * days stack upward inside their own column, memory clusters by department with a height that grows with its busiest day.
 * Differences: the lanes are ours (our five outer tiers sit above Applications, in the rings' order), the AGENTS md lane
 * carries the instruction file's edits as ticks, and "As it was on" (D328) moves the anchor: what changed after the
 * point is not placed, because on that day it did not exist as it is now.
 */
import { monthName } from '../../dateTimeFormat.js';
import { ARMS_THEMES } from './armsGroupingModel.js';
import { EXTRA_LAYERS, type Globe, type GlobeLayer, LAYER_WORDS } from './memoryGlobeModel.js';

const DAY = 86_400_000;
export const TIMELINE_MONTHS = 3;

export type TimelineLaneKey = GlobeLayer | 'instructions';

/** The lanes from the top: the five outer tiers, then the reference's Applications, Routines, AGENTS md, Memory, Skills. */
export const LANE_ORDER: readonly TimelineLaneKey[] = [...EXTRA_LAYERS, 'apps', 'routines', 'instructions', 'memory', 'skills'];
/** The lanes under five themes: the theme's tiers together, the instruction file with memory. */
export const THEME_LANE_ORDER: readonly TimelineLaneKey[] = ['roadmap', 'plans', 'decisions', 'architecture', 'library', 'apps', 'routines', 'skills', 'instructions', 'memory'];
export const laneWords = (k: TimelineLaneKey): string => (k === 'instructions' ? 'AGENTS md' : LAYER_WORDS[k]);

export interface TimelineOptions {
  width: number;
  height: number;
  /** Where the header begins (the stage's own top bar). */
  top?: number;
  /** "As it was on": the point everything is read at. Absent: now (the newest change). */
  asOf?: number;
  /** The clock, for a timeline with nothing dated. */
  now?: number;
  /** How many months to show; absent, three, or two in a narrow room. */
  months?: number;
  /** Local time's offset from UTC, in minutes (`-new Date().getTimezoneOffset()`); 0 is UTC. */
  tzOffsetMinutes?: number;
  /** Layers and departments switched off in the legend. */
  offLayers?: ReadonlySet<GlobeLayer>;
  offDepartments?: ReadonlySet<number>;
  /** When the instruction file (AGENTS.md / CLAUDE.md) was edited, ms: one tick per edit day on its own lane. Left out, there is no such lane (the console's project memory has no instruction file). */
  instructionEdits?: readonly number[];
}

export interface TimelinePos {
  x: number;
  y: number;
  lane: GlobeLayer;
  /** `yyyy-mm-dd` of its day, or `older`. */
  day: string;
  /** An icon (tools, routines) or a dot (everything else). */
  icon: boolean;
  r: number;
}

/** A theme's heading over its lanes (five themes): where its bar runs. */
export interface TimelineThemeHead {
  id: string;
  title: string;
  /** The first of its tiers, for the theme's colour. */
  layer: GlobeLayer;
  top: number;
  bottom: number;
}

export interface TimelineLane {
  key: TimelineLaneKey;
  label: string;
  top: number;
  bottom: number;
}

export interface TimelineCluster {
  deptIndex: number;
  top: number;
  bottom: number;
  count: number;
}

export interface TimelineDay {
  key: string;
  x: number;
  day: number;
  dow: number;
}

/** An instruction-file edit day: a tick on its lane, taller when the file was edited more than once that day. */
export interface TimelineTick {
  x: number;
  day: string;
  count: number;
}

export interface TimelineLayout {
  /** Node index to place; a node that is switched off, undated-and-excluded or after the point has none. */
  positions: Map<number, TimelinePos>;
  lanes: TimelineLane[];
  /** Under five themes, each theme's heading over the lanes of its tiers; empty under nine tiers. */
  themes: TimelineThemeHead[];
  clusters: TimelineCluster[];
  months: Array<{ label: string; x: number; x1: number; year: number }>;
  days: TimelineDay[];
  dayW: number;
  today?: { x: number; key: string };
  anchor: number;
  /** The instruction file's edits inside the shown months, and how many fell before them (in the Older block). */
  ticks: TimelineTick[];
  olderEdits: number;
  bounds: { left: number; right: number; top: number; bottom: number; olderX: number; olderW: number; start: string; end: string };
  /** Counts the page says in words. */
  dated: number;
  older: number;
  afterPoint: number;
}

const pad = (n: number) => String(n).padStart(2, '0');
/** The day a time falls on, in the zone the offset names. */
export function dayKeyOf(t: number, tzOffsetMinutes = 0): string {
  const d = new Date(t + tzOffsetMinutes * 60_000);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}
const fractionOf = (t: number, tz: number) => {
  const d = new Date(t + tz * 60_000);
  return (d.getUTCHours() * 60 + d.getUTCMinutes()) / 1440;
};

/** Build the Timeline for a globe's nodes. */
export function buildTimeline(globe: Globe, opts: TimelineOptions): TimelineLayout {
  const tz = opts.tzOffsetMinutes ?? 0;
  const W = Math.max(320, opts.width);
  const H = Math.max(260, opts.height);
  const off = opts.offLayers ?? new Set<GlobeLayer>();
  const offDept = opts.offDepartments ?? new Set<number>();
  const shown = globe.nodes.filter((n) => !n.struct && !off.has(n.layer) && !(n.deptIndex >= 0 && offDept.has(n.deptIndex)));

  const point = opts.asOf;
  let afterPoint = 0;
  const live = shown.filter((n) => {
    if (point !== undefined && n.changedAt !== undefined && n.changedAt > point) {
      afterPoint++;
      return false;
    }
    return true;
  });
  const edits = (opts.instructionEdits ?? []).filter((t) => point === undefined || t <= point);
  const newest = Math.max(live.reduce((m, n) => (n.changedAt !== undefined && n.changedAt > m ? n.changedAt : m), 0), edits.reduce((m, t) => Math.max(m, t), 0));
  const anchor = point ?? (newest || opts.now || Date.now());
  const [ay, am] = dayKeyOf(anchor, tz).split('-').map(Number);

  const months: TimelineLayout['months'] = [];
  const monthDays: number[] = [];
  const monthStarts: Array<{ y: number; m: number }> = [];
  // A narrow room shows two months, so a day keeps a readable column.
  const nMonths = opts.months ?? (W < 760 ? 2 : TIMELINE_MONTHS);
  for (let i = nMonths - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(ay, am - 1 - i, 1));
    const days = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
    monthDays.push(days);
    monthStarts.push({ y: d.getUTCFullYear(), m: d.getUTCMonth() });
  }
  const totalDays = monthDays.reduce((a, b) => a + b, 0);
  const firstKey = `${monthStarts[0].y}-${pad(monthStarts[0].m + 1)}-01`;

  // Horizontal: lane label column, the Older block, then equal days in month blocks.
  const left = 28;
  const labelW = 118;
  const olderX = left + labelW;
  const olderW = Math.max(90, Math.min(200, W * 0.125));
  const gapOlder = 22;
  const monthGap = 12;
  const right = W - 28;
  const daysX = olderX + olderW + gapOlder;
  const dayW = Math.max(2.4, (right - daysX - monthGap * (nMonths - 1)) / totalDays);
  const days = new Map<string, TimelineDay>();
  let x = daysX;
  monthStarts.forEach((ms, mi) => {
    const x0 = x;
    for (let d = 1; d <= monthDays[mi]; d++) {
      const key = `${ms.y}-${pad(ms.m + 1)}-${pad(d)}`;
      days.set(key, { key, x, day: d, dow: new Date(Date.UTC(ms.y, ms.m, d)).getUTCDay() });
      x += dayW;
    }
    months.push({ label: monthName(Date.UTC(ms.y, ms.m, 1), 'long', { timeZone: 'UTC' }), x: x0, x1: x, year: ms.y });
    x += monthGap;
  });
  const todayKey = dayKeyOf(anchor, tz);
  const today = days.get(todayKey);
  const where = (n: { changedAt?: number }): TimelineDay | null => {
    if (n.changedAt === undefined) return null;
    const key = dayKeyOf(n.changedAt, tz);
    return key < firstKey ? null : (days.get(key) ?? null);
  };

  // Ticks of the instruction file: one per day it was edited.
  const tickDays = new Map<string, number>();
  let olderEdits = 0;
  for (const t of edits) {
    const key = dayKeyOf(t, tz);
    const d = days.get(key);
    if (d) tickDays.set(key, (tickDays.get(key) ?? 0) + 1);
    else if (key < firstKey) olderEdits++;
  }
  const ticks: TimelineTick[] = [...tickDays.entries()].map(([key, count]) => ({ x: days.get(key)!.x + dayW / 2, day: key, count })).sort((a, b) => a.x - b.x);

  // Vertical: lanes with something in them (the core four always, the outer tiers when they hold something).
  const top = opts.top ?? 0;
  const chartTop = top + 74;
  const bottom = H - 40;
  const avail = Math.max(300, bottom - chartTop);
  const gap = 10;
  const iconSize = Math.max(14, Math.min(22, dayW * 1.5));
  const olderPitch = 22;
  const olderCols = Math.max(1, Math.floor((olderW - 6) / olderPitch));
  const pick = (k: GlobeLayer) => live.filter((n) => n.layer === k);
  const iconNeed = (k: GlobeLayer) => {
    const older = pick(k).filter((n) => !where(n)).length;
    return Math.max(iconSize + 14, Math.ceil(older / olderCols) * olderPitch + 10);
  };
  const laneKeys = (globe.grouping === 'themes' ? THEME_LANE_ORDER : LANE_ORDER).filter((k) => (k === 'instructions' ? opts.instructionEdits !== undefined : globe.counts[k] > 0 || k === 'apps' || k === 'routines' || k === 'memory' || k === 'skills')).filter((k) => k === 'instructions' || !off.has(k));
  const extraLanes = laneKeys.filter((k): k is GlobeLayer => (EXTRA_LAYERS as readonly string[]).includes(k)).length;
  const dotLane = Math.max(20, Math.min(32, (avail * 0.24) / Math.max(1, extraLanes)));
  const heights = new Map<TimelineLaneKey, number>();
  for (const k of laneKeys) {
    if (k === 'instructions') heights.set(k, 34);
    else if ((EXTRA_LAYERS as readonly string[]).includes(k)) heights.set(k, dotLane);
    else if (k === 'apps') heights.set(k, Math.min(avail * 0.14, iconNeed('apps')));
    else if (k === 'routines') heights.set(k, Math.min(avail * 0.18, globe.counts.routines ? Math.max(iconNeed('routines'), avail * 0.08) : 32));
    else if (k === 'skills') heights.set(k, Math.max(40, avail * 0.07));
  }
  const used = [...heights.values()].reduce((a, b) => a + b, 0) + gap * Math.max(0, laneKeys.length - 1);
  if (laneKeys.includes('memory')) heights.set('memory', Math.max(96, avail - used));
  let y = chartTop;
  const lanes: TimelineLane[] = laneKeys.map((key) => {
    const h = heights.get(key) ?? 0;
    const lane = { key, label: laneWords(key), top: y, bottom: y + h };
    y += h + gap;
    return lane;
  });
  const laneOf = (k: TimelineLaneKey) => lanes.find((l) => l.key === k);
  const themes: TimelineThemeHead[] = [];
  if (globe.grouping === 'themes') {
    for (const theme of ARMS_THEMES) {
      const mine = lanes.filter((l) => (theme.tiers as readonly string[]).includes(l.key) || (theme.id === 'memory' && l.key === 'instructions'));
      if (mine.length) themes.push({ id: theme.id, title: theme.title, layer: theme.tiers[0], top: mine[0].top, bottom: mine[mine.length - 1].bottom });
    }
  }
  const positions = new Map<number, TimelinePos>();

  const dotPitch = 5;
  const dotR = 2;
  const dayCols = dayW >= 11 ? 2 : 1;
  const olderDotCols = Math.max(1, Math.floor((olderW - 6) / dotPitch));
  const memory = laneOf('memory');
  const memNodes = pick('memory');
  const clusters = globe.departments
    .map((d) => ({ deptIndex: d.index, nodes: memNodes.filter((n) => n.deptIndex === d.index) }))
    .filter((c) => c.nodes.length > 0);
  const need = (nodes: typeof memNodes) => {
    const per = new Map<string, number>();
    let older = 0;
    for (const n of nodes) {
      const d = where(n);
      if (d) per.set(d.key, (per.get(d.key) ?? 0) + 1);
      else older++;
    }
    return Math.max(4, Math.ceil(Math.max(0, ...per.values()) / dayCols), Math.ceil(older / olderDotCols));
  };
  const cgap = 8;
  const needs = clusters.map((c) => need(c.nodes));
  const placed: TimelineCluster[] = [];
  if (memory) {
    // A line's room at the top for the lane's own name.
    const usable = memory.bottom - memory.top - 18 - cgap * Math.max(0, clusters.length - 1) - 4;
    const sqrtSum = needs.reduce((s, v) => s + Math.sqrt(v), 0) || 1;
    let cy = memory.top + 20;
    clusters.forEach((c, i) => {
      const h = Math.max(9, (usable * Math.sqrt(needs[i])) / sqrtSum);
      placed.push({ deptIndex: c.deptIndex, top: cy, bottom: cy + h, count: c.nodes.length });
      cy += h + cgap;
    });
    const last = placed.at(-1);
    // Too many topics for the lane: every cluster shrinks by the same share of what is there.
    const fix = last && last.bottom > memory.bottom - 2 ? (memory.bottom - 2 - memory.top) / (last.bottom - memory.top) : 1;
    if (fix < 1) for (const c of placed) {
      c.top = memory.top + (c.top - memory.top) * fix;
      c.bottom = memory.top + (c.bottom - memory.top) * fix;
    }
  }

  /** Each band fills its rows bottom-up, inside the right day column. */
  const place = (lane: GlobeLayer, nodes: typeof live, o: { icon: boolean; top: number; bottom: number; r: number }) => {
    const groups = new Map<string, typeof live>();
    for (const n of nodes) {
      const d = where(n);
      const k = d ? d.key : 'older';
      const list = groups.get(k);
      if (list) list.push(n);
      else groups.set(k, [n]);
    }
    const height = o.bottom - o.top - 6;
    for (const [k, list] of groups) {
      list.sort((a, b) => (a.changedAt ?? 0) - (b.changedAt ?? 0) || a.id.localeCompare(b.id));
      const older = k === 'older';
      if (o.icon) {
        const size = older ? 18 : iconSize;
        const full = older ? olderPitch : iconSize + 2;
        const cols = older ? olderCols : 1;
        const rows = Math.ceil(list.length / cols);
        const pitch = rows > 1 ? Math.min(full, (height - size) / (rows - 1)) : full;
        list.forEach((n, i) => {
          const col = i % cols;
          const row = Math.floor(i / cols);
          const cx = older ? olderX + 4 + olderPitch / 2 + col * olderPitch : days.get(k)!.x + dayW / 2;
          positions.set(n.index, { x: cx, y: o.bottom - 4 - size / 2 - row * pitch, lane, day: k, icon: true, r: size / 2 });
        });
      } else {
        const cols = older ? olderDotCols : dayCols;
        const rows = Math.ceil(list.length / cols);
        const pitch = Math.min(dotPitch, rows > 1 ? (height - o.r * 2) / (rows - 1) : dotPitch);
        list.forEach((n, i) => {
          const col = i % cols;
          const row = Math.floor(i / cols);
          const cx = older ? olderX + 4 + dotPitch / 2 + col * dotPitch : days.get(k)!.x + (cols === 2 ? dayW * (col ? 0.69 : 0.31) : dayW / 2);
          positions.set(n.index, { x: cx, y: o.bottom - 3 - o.r - row * Math.max(0.5, pitch), lane, day: k, icon: false, r: o.r });
        });
      }
    }
  };
  for (const k of ['apps', 'routines'] as const) {
    const lane = laneOf(k);
    if (lane) place(k, pick(k), { icon: true, top: lane.top, bottom: lane.bottom, r: 0 });
  }
  for (const k of [...EXTRA_LAYERS, 'skills'] as const) {
    const lane = laneOf(k);
    if (lane) place(k, pick(k), { icon: false, top: lane.top, bottom: lane.bottom, r: dotR });
  }
  if (memory) placed.forEach((c, i) => place('memory', clusters[i].nodes, { icon: false, top: c.top, bottom: c.bottom, r: dotR }));

  let dated = 0;
  for (const n of live) if (where(n)) dated++;
  const endMonth = monthStarts.at(-1)!;
  return {
    positions,
    lanes,
    themes,
    clusters: placed,
    months,
    days: [...days.values()],
    dayW,
    ...(today ? { today: { x: today.x + dayW * fractionOf(anchor, tz), key: todayKey } } : {}),
    anchor,
    ticks,
    olderEdits,
    bounds: { left: olderX, right, top: chartTop, bottom: y - gap, olderX, olderW, start: firstKey, end: `${endMonth.y}-${pad(endMonth.m + 1)}-${pad(monthDays.at(-1)!)}` },
    dated,
    older: live.length - dated,
    afterPoint,
  };
}

/** "Changed 3 days ago", for a tooltip. */
export function changedWords(changedAt: number | undefined, now: number): string {
  if (changedAt === undefined) return 'No change date is known yet';
  const days = Math.floor((now - changedAt) / DAY);
  if (days <= 0) return 'Changed today';
  if (days === 1) return 'Changed yesterday';
  if (days < 60) return `Changed ${days} days ago`;
  return `Changed ${Math.round(days / 30)} months ago`;
}
